import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import EmptyState from '@/components/base/EmptyState';
import SectionHeading from '@/components/base/SectionHeading';
import WorkCard from '@/components/base/WorkCard';
import { getHotWorks, searchWorks } from '@/utils/content';

const hotKeywords = ['悬疑', '科幻', '古装', '美食', '家庭', '历史'];

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const query = params.get('q') ?? '';
  const [input, setInput] = useState(query);

  useEffect(() => {
    setInput(query);
  }, [query]);

  useEffect(() => {
    document.title = query
      ? `“${query}”的搜索结果 - 长日光阴`
      : '影视搜索 - 长日光阴影视推荐平台';
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute('content', `在长日光阴搜索影视作品，${query ? `当前关键词：${query}` : '支持片名、类型、地区、主演等多维度检索'}。`);
  }, [query]);

  const results = query.trim() ? searchWorks(query) : [];
  const hot = getHotWorks(4);

  const submitSearch = (keyword: string) => {
    const next = keyword.trim();
    if (next) {
      setParams({ q: next });
    } else {
      setParams({});
    }
  };

  return (
    <div className="pb-16">
      <section className="w-full border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full px-4 py-10 md:px-6 md:py-14">
          <h1 className="font-heading text-2xl font-black text-foreground-950 md:text-3xl">影视搜索</h1>
          <p className="mt-2 text-sm text-foreground-600">输入片名、类型、地区或主创，快速定位你想了解的作品。</p>

          <form
            className="mt-6 flex max-w-2xl items-center gap-2 rounded-md border border-background-300 bg-background-50 p-1.5"
            onSubmit={(event) => {
              event.preventDefault();
              submitSearch(input);
            }}
          >
            <span className="flex h-9 w-9 items-center justify-center text-secondary-500">
              <i className="ri-search-line text-lg" aria-hidden="true" />
            </span>
            <input
              type="search"
              value={input}
              onChange={(event) => setInput(event.target.value)}
              placeholder="例如：悬疑、科幻、长夜将明"
              className="min-w-0 flex-1 bg-transparent text-sm text-foreground-900 outline-none placeholder:text-secondary-400"
              aria-label="搜索关键词"
            />
            <button
              type="submit"
              className="shrink-0 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              搜索
            </button>
          </form>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span className="text-xs text-secondary-500">热门搜索：</span>
            {hotKeywords.map((keyword) => (
              <button
                key={keyword}
                type="button"
                onClick={() => submitSearch(keyword)}
                className="whitespace-nowrap rounded-full bg-background-200 px-3 py-1 text-xs text-foreground-700 transition-colors hover:bg-secondary-200"
              >
                {keyword}
              </button>
            ))}
          </div>
        </div>
      </section>

      <div className="mx-auto w-full px-4 py-6 md:px-6">
        <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '搜索结果' }]} />
      </div>

      <div className="mx-auto w-full px-4 md:px-6">
        {query.trim() ? (
          results.length > 0 ? (
            <>
              <p className="mb-5 text-sm text-foreground-600">
                关键词“<span className="font-semibold text-primary-600">{query}</span>”共找到{' '}
                <span className="font-bold text-primary-600">{results.length}</span> 部作品
              </p>
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-5 lg:grid-cols-4">
                {results.map((work) => (
                  <WorkCard key={work.id} work={work} showSynopsis />
                ))}
              </div>
            </>
          ) : (
            <div className="space-y-10">
              <EmptyState
                icon="ri-search-eye-line"
                title="没有找到匹配的作品"
                description={`没有与“${query}”相关的结果，试试更换关键词，或看看下面的热门推荐。`}
              />
              <div>
                <SectionHeading title="热门推荐" description="这些作品一直很受欢迎，不妨从它们开始。" />
                <div className="grid grid-cols-2 gap-4 md:grid-cols-4 md:gap-5">
                  {hot.map((work) => (
                    <WorkCard key={work.id} work={work} />
                  ))}
                </div>
              </div>
            </div>
          )
        ) : (
          <div className="space-y-10">
            <div className="rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-12 text-center">
              <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-secondary-500">
                <i className="ri-search-line text-2xl" aria-hidden="true" />
              </span>
              <h3 className="mt-4 font-heading text-base font-bold text-foreground-900">开始你的搜索</h3>
              <p className="mt-2 text-sm text-foreground-600">输入关键词，发现更多值得一看的影视作品。</p>
            </div>
            <div>
              <SectionHeading title="大家都在看" description="热门作品一览，点击卡片直达剧情介绍。" />
              <div className="grid grid-cols-2 gap-4 md:grid-cols-4 md:gap-5">
                {hot.map((work) => (
                  <WorkCard key={work.id} work={work} />
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}